var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { autoUpdate, flip, FloatingFocusManager, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useInteractions, useListNavigation, useRole, useTypeahead, } from '@floating-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CaretDown } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { colors, cssVarsThemed, useDirection } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { getClosedStyles, getDefaultGlobalPopoverMountElement, PopoverBase, usePopoverTransition, } from '@creditornot/cb-popover';
import { createCssVars, themedConfigToThemedAttrs } from '@creditornot/cb-utils';
import Button, { variantConfigs as baseVariantConfigs, cssVars } from './Button';
/**
 * See https://developer.mozilla.org/en-US/docs/Web/API/Navigator/platform#examples
 * `navigator.platform` should almost always be avoided in favor of feature detection.
 * But there is one case where, among the options you could use, `navigator.platform`
 * may be the least-bad option: When you need to show users advice about whether the
 * modifier key for keyboard shortcuts is the ⌘ command key (found on Apple systems)
 * rather than the ⌃ control key (on non-Apple systems)
 */
var isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
// Map from the `KeyboardEvent.key` to the visual representation.
var KEYMAP = {
    Alt: isMac ? '⌥' : 'Alt',
    CmdOrCtrl: isMac ? '⌘' : 'Ctrl',
    Control: 'Ctrl',
    Shift: '⇧ Shift',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
};
var MODIFIER_KEYS = {
    Alt: 'altKey',
    Control: 'ctrlKey',
    Meta: 'metaKey',
    Shift: 'shiftKey',
};
var variantConfigs = {
    blue: {
        light: __assign(__assign({}, baseVariantConfigs.blue.light), { middleBorder: colors.salt12 }),
        dark: __assign(__assign({}, baseVariantConfigs.blue.dark), { middleBorder: colors.salt16 }),
    },
    lightBlue: {
        light: __assign(__assign({}, baseVariantConfigs.lightBlue.light), { middleBorder: colors.wolt16 }),
        dark: __assign(__assign({}, baseVariantConfigs.lightBlue.dark), { middleBorder: colors.wolt16 }),
    },
};
var kbdConfigs = {
    kbdBackground: { light: colors.salt92, dark: colors.pepper100 },
    kbdColor: { light: colors.pepper68, dark: colors.salt76 },
    kbdBorder: { light: colors.pepper20, dark: colors.pepper92 },
};
var toggleCssVarOverrides = {
    horizontalPadding: '16px',
};
var variantCssVars = createCssVars(['background', 'middleBorder']);
var kbdCssVars = createCssVars(['kbdBackground', 'kbdColor', 'kbdBorder']);
var Root = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n\n  position: relative;\n  z-index: 0;\n  box-sizing: border-box;\n  display: inline-flex;\n  width: ", ";\n  max-width: 100%;\n  border-radius: 8px;\n"], ["\n  ", "\n\n  position: relative;\n  z-index: 0;\n  box-sizing: border-box;\n  display: inline-flex;\n  width: ", ";\n  max-width: 100%;\n  border-radius: 8px;\n"])), function (_a) {
    var $variant = _a.$variant;
    return cssVarsThemed(themedConfigToThemedAttrs(variantConfigs[$variant]));
}, function (_a) {
    var $stretch = _a.$stretch;
    return ($stretch ? '100%' : 'auto');
});
var PrimaryButton = styled(Button)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  flex: 1;\n  border-start-end-radius: 0;\n  border-end-end-radius: 0;\n\n  &:focus {\n    z-index: 1;\n  }\n"], ["\n  flex: 1;\n  border-start-end-radius: 0;\n  border-end-end-radius: 0;\n\n  &:focus {\n    z-index: 1;\n  }\n"])));
var MiddleBorder = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  width: 1px;\n  background: ", ";\n  position: relative;\n\n  &::after {\n    position: absolute;\n    content: '';\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    background: ", ";\n  }\n\n  &[data-disabled] {\n    opacity: 0.8;\n    cursor: not-allowed;\n  }\n"], ["\n  width: 1px;\n  background: ", ";\n  position: relative;\n\n  &::after {\n    position: absolute;\n    content: '';\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    background: ", ";\n  }\n\n  &[data-disabled] {\n    opacity: 0.8;\n    cursor: not-allowed;\n  }\n"])), variantCssVars.background, variantCssVars.middleBorder);
var Toggle = styled(Button)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  position: relative;\n  border-start-start-radius: 0;\n  border-end-start-radius: 0;\n  z-index: 0;\n\n  svg {\n    display: block;\n    fill: currentColor;\n    width: ", ";\n  }\n"], ["\n  position: relative;\n  border-start-start-radius: 0;\n  border-end-start-radius: 0;\n  z-index: 0;\n\n  svg {\n    display: block;\n    fill: currentColor;\n    width: ", ";\n  }\n"])), cssVars.iconWidth);
var defaultMenuPortalElement = getDefaultGlobalPopoverMountElement();
var Menu = styled(PopoverBase)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ", "\n  min-width: 160px;\n  max-width: 320px;\n  padding: 8px 16px;\n"], ["\n  ", "\n  min-width: 160px;\n  max-width: 320px;\n  padding: 8px 16px;\n"])), cssVarsThemed(kbdConfigs));
var Kbd = styled.kbd(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  ", "\n  display: inline-block;\n  padding: 2px 4px;\n  border: ", " 1px solid;\n  border-radius: 4px;\n  background: ", ";\n  color: ", ";\n\n  &:not(:first-child) {\n    margin-inline-start: 4px;\n  }\n"], ["\n  ", "\n  display: inline-block;\n  padding: 2px 4px;\n  border: ", " 1px solid;\n  border-radius: 4px;\n  background: ", ";\n  color: ", ";\n\n  &:not(:first-child) {\n    margin-inline-start: 4px;\n  }\n"])), typographyCss.Small, kbdCssVars.kbdBorder, kbdCssVars.kbdBackground, kbdCssVars.kbdColor);
var Shortcut = function (_a) {
    var keys = _a.keys;
    var displayedKeys = (Array.isArray(keys) ? keys : [keys]).map(function (key) {
        return key in KEYMAP ? KEYMAP[key] : key;
    });
    return (React.createElement(React.Fragment, null, displayedKeys.map(function (key, index) { return (React.createElement(Kbd, { key: index }, key)); })));
};
/**
 * Check that specified modifiers match, and that no extra modifiers are pressed
 */
var checkExactModifiersArePressed = function (event, keyCodes) {
    var modifiers = Object.keys(MODIFIER_KEYS);
    return !modifiers.some(function (modifier) {
        return (keyCodes.includes(modifier) && !event[MODIFIER_KEYS[modifier]]) ||
            (!keyCodes.includes(modifier) && event[MODIFIER_KEYS[modifier]]);
    });
};
var matchesShortcut = function (event, shortcut) {
    if (!shortcut)
        return false;
    var keyCodes = (Array.isArray(shortcut) ? shortcut : [shortcut]).map(function (key) {
        // With Alt pressed, `event.key` may map to a special character and not the actual
        // key name, thus we need to check for `event.code` instead.
        if (/^\d$/.test(key))
            return "Digit".concat(key);
        if (/^[A-Z]$/.test(key))
            return "Key".concat(key);
        if (key === 'CmdOrCtrl')
            return isMac ? 'Meta' : 'Control';
        return key;
    });
    return keyCodes.includes(event.code) && checkExactModifiersArePressed(event, keyCodes);
};
var useKeyboardShortcuts = function (_a) {
    var enabled = _a.enabled, handleSelect = _a.handleSelect, menuItems = _a.menuItems;
    useEffect(function () {
        var shortcutActions = menuItems.filter(function (item) { return item.shortcut; });
        if (enabled && shortcutActions.length) {
            var listener_1 = function (event) {
                shortcutActions.forEach(function (_a) {
                    var onClick = _a.onClick, shortcut = _a.shortcut;
                    if (matchesShortcut(event, shortcut))
                        handleSelect(onClick);
                });
            };
            document.addEventListener('keydown', listener_1);
            return function () {
                document.removeEventListener('keydown', listener_1);
            };
        }
        return;
    }, [handleSelect, menuItems, enabled]);
};
/**
 * A Split button is a multi-function menu button that offers a default action as well as the option
 * of choosing from a set of related actions. Split Buttons carry the same color styles and states
 * as our base button component.
 */
var SplitButton = React.forwardRef(function (_a, ref) {
    var cssVarOverrides = _a.cssVarOverrides, _b = _a.disabled, disabled = _b === void 0 ? false : _b, _c = _a.keyboardShortcutHandlers, keyboardShortcutHandlers = _c === void 0 ? 'global' : _c, _d = _a.menuButtonAriaLabel, menuButtonAriaLabel = _d === void 0 ? 'Other actions' : _d, menuItems = _a.menuItems, menuPortalElement = _a.menuPortalElement, _e = _a.role, primaryButtonRole = _e === void 0 ? 'button' : _e, _f = _a.size, size = _f === void 0 ? 'small' : _f, _g = _a.stretch, stretch = _g === void 0 ? false : _g, style = _a.style, _h = _a.type, type = _h === void 0 ? 'button' : _h, _j = _a.variant, variant = _j === void 0 ? 'blue' : _j, rest = __rest(_a, ["cssVarOverrides", "disabled", "keyboardShortcutHandlers", "menuButtonAriaLabel", "menuItems", "menuPortalElement", "role", "size", "stretch", "style", "type", "variant"]);
    var rtl = useDirection() === 'rtl';
    var _k = __read(useState(false), 2), isFocused = _k[0], setIsFocused = _k[1];
    var _l = __read(useState(false), 2), isOpen = _l[0], setIsOpen = _l[1];
    var _m = __read(useState(null), 2), activeIndex = _m[0], setActiveIndex = _m[1];
    var _o = __read(useState(null), 2), selectedIndex = _o[0], setSelectedIndex = _o[1];
    var listRef = useRef([]);
    var listContentRef = useRef(menuItems.map(function (item) { return item.label; }));
    var isTypingRef = useRef(false);
    var _p = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'bottom-end',
        middleware: [shift(), offset({ mainAxis: 4 }), flip()],
        whileElementsMounted: autoUpdate,
    }), context = _p.context, placement = _p.placement, refs = _p.refs, strategy = _p.strategy, x = _p.x, y = _p.y;
    var click = useClick(context);
    var dismiss = useDismiss(context);
    var listRole = useRole(context, { role: 'menu' });
    var keyboardShortcutsEnabled = keyboardShortcutHandlers === 'global' ||
        (keyboardShortcutHandlers === 'whenInFocus' && isFocused);
    var listNav = useListNavigation(context, {
        listRef: listRef,
        activeIndex: activeIndex,
        selectedIndex: selectedIndex,
        onNavigate: setActiveIndex,
        rtl: rtl,
    });
    var placementSide = placement.split('-')[0];
    var typeahead = useTypeahead(context, {
        listRef: listContentRef,
        activeIndex: activeIndex,
        selectedIndex: selectedIndex,
        onMatch: isOpen ? setActiveIndex : setSelectedIndex,
        onTypingChange: function (isTyping) {
            isTypingRef.current = isTyping;
        },
    });
    var _q = useInteractions([
        click,
        dismiss,
        listRole,
        listNav,
        typeahead,
    ]), getFloatingProps = _q.getFloatingProps, getItemProps = _q.getItemProps, getReferenceProps = _q.getReferenceProps;
    var _r = usePopoverTransition(context), isMounted = _r.isMounted, styles = _r.styles;
    var handleSelect = useCallback(function (callback) {
        setIsOpen(false);
        callback();
    }, []);
    // clear selection when menu is closed
    useEffect(function () {
        if (!isOpen) {
            setActiveIndex(null);
        }
    }, [isOpen]);
    useEffect(function () {
        listContentRef.current = menuItems.map(function (item) { return item.label; });
    }, [menuItems]);
    useKeyboardShortcuts({ menuItems: menuItems, enabled: keyboardShortcutsEnabled, handleSelect: handleSelect });
    return (React.createElement(React.Fragment, null,
        React.createElement(Root, { "$stretch": stretch, "$variant": variant, onBlur: function () { return setIsFocused(isOpen); }, onFocus: function () { return setIsFocused(true); }, style: style },
            React.createElement(PrimaryButton, __assign({ cssVarOverrides: cssVarOverrides, disabled: disabled, role: primaryButtonRole, size: size, type: type, variant: variant }, rest, { ref: ref })),
            React.createElement(MiddleBorder, { "data-disabled": disabled ? true : undefined }),
            React.createElement(Toggle, __assign({ "aria-label": menuButtonAriaLabel, cssVarOverrides: __assign(__assign({}, toggleCssVarOverrides), (cssVarOverrides || {})), disabled: disabled, size: size, variant: variant }, getReferenceProps({ ref: refs.setReference })),
                React.createElement(CaretDown, null))),
        React.createElement(FloatingPortal, { root: menuPortalElement !== null && menuPortalElement !== void 0 ? menuPortalElement : defaultMenuPortalElement }, isMounted && (React.createElement(FloatingFocusManager, { context: context, modal: false },
            React.createElement(Menu, __assign({ style: __assign(__assign(__assign({}, getClosedStyles({ side: placementSide })), styles), { position: strategy, top: y !== null && y !== void 0 ? y : 0, left: x !== null && x !== void 0 ? x : 0, overflowY: 'auto' }) }, getFloatingProps({ ref: refs.setFloating })),
                React.createElement(MenuItemGroup, null, menuItems.map(function (_a, index) {
                    var disabled = _a.disabled, label = _a.label, onClick = _a.onClick, shortcut = _a.shortcut, _b = _a.variant, variant = _b === void 0 ? 'black' : _b, menuItemProps = __rest(_a, ["disabled", "label", "onClick", "shortcut", "variant"]);
                    return (React.createElement(MenuActionItem, __assign({ key: index, variant: variant }, menuItemProps, { "aria-current": index === activeIndex ? true : undefined, focused: index === activeIndex, ref: function (node) {
                            listRef.current[index] = node;
                        }, rightContent: shortcut && React.createElement(Shortcut, { keys: shortcut }), role: "menuitem", tabIndex: index === activeIndex ? 0 : -1 }, getItemProps({
                        disabled: disabled,
                        // Handle pointer select.
                        onClick: function () {
                            handleSelect(onClick);
                        },
                        // Handle keyboard select.
                        onKeyDown: function (event) {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                // Prevent firing the action twice if possible shortcut contains the enter key
                                if (!keyboardShortcutsEnabled ||
                                    menuItems.every(function (item) {
                                        return !matchesShortcut(
                                        // TODO: remove type assertion once React 16 support is dropped, see https://github.com/facebook/react/pull/18287
                                        event, item.shortcut);
                                    })) {
                                    handleSelect(onClick);
                                }
                            }
                            if (event.key === ' ' && !isTypingRef.current) {
                                event.preventDefault();
                            }
                        },
                        onKeyUp: function (event) {
                            if (event.key === ' ' && !isTypingRef.current) {
                                handleSelect(onClick);
                            }
                        },
                    })), label));
                }))))))));
});
SplitButton.displayName = 'SplitButton';
export default SplitButton;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
//# sourceMappingURL=SplitButton.js.map